// @ts-nocheck: Suppress router typing noise caused by version mismatch between preact + preact-iso; routes work at runtime.
import "./app.css";
import { posthog } from "posthog-js";
import { LocationProvider, Route, Router, useLocation } from "preact-iso";
import hydrate from "preact-iso/hydrate";
import { Toaster } from "react-hot-toast";
import { posthogId } from "../../constants.ts";
import { isIgnorable } from "./errorReporting.ts";
import { AppErrorBoundary } from "./errorBoundary.tsx";
import { BotSwitcher } from "./bots.tsx";
import { ChatWidget } from "./chatWidget.tsx";
import { ConfirmDialog, LegalFooter } from "./components.tsx";
import { Header, NotFound } from "./header.tsx";
import {
  ensureUser,
  usePendingAuthToken,
  useTokenLogin,
} from "./loginHooks.ts";
import { captureGoogleAnalyticsIds } from "./googleAnalyticsIntegration.ts";
import { routes } from "./routes.tsx";
import { prerenderedRoutes } from "./paths.ts";
import { captureReferralFromUrl } from "./utils.ts";

if (typeof Object.hasOwn !== "function") {
  Object.hasOwn = (obj: object, prop: PropertyKey) =>
    Object.prototype.hasOwnProperty.call(obj, prop);
}

if (typeof window !== "undefined") {
  const originalDefine = customElements.define;
  customElements.define = function (name, constructor, options) {
    if (!customElements.get(name)) {
      originalDefine.call(customElements, name, constructor, options);
    }
  };

  posthog.init(posthogId, {
    api_host: "https://us.posthog.com",
    before_send: (event) => {
      if (event?.event === "$exception") {
        const exceptionValues = event.properties?.$exception_values ?? [];
        const exceptionList = event.properties?.$exception_list ?? [];
        const isBad = exceptionValues.some(isIgnorable) ||
          exceptionList.some((e: { value?: string }) => isIgnorable(e?.value));
        if (isBad) return null;
      }
      return event;
    },
  });
  captureGoogleAnalyticsIds();
  captureReferralFromUrl();
}

const App = () => {
  const { path } = useLocation();
  if (typeof window !== "undefined") ensureUser();
  usePendingAuthToken();
  useTokenLogin();
  return (
    <>
      <Toaster
        toastOptions={{
          className:
            "!bg-surface-raised-light dark:!bg-surface-raised !text-text-primary-light dark:!text-text-primary",
        }}
      />
      <BotSwitcher />
      <ChatWidget />
      <ConfirmDialog />
      <div
        className={`flex flex-col gap-3 p-3 font-body bg-surface-light dark:bg-surface text-text-primary-light dark:text-text-primary ${
          path === "/chat" || path === "/builder-chat" ||
            path === "/auto-landing"
            ? "h-dvh overflow-hidden"
            : "min-h-dvh"
        }`}
      >
        <div className="sticky top-0 z-50 bg-surface-light dark:bg-surface">
          <Header />
        </div>
        <AppErrorBoundary>
          <div
            className={`flex flex-col ${
              path === "/chat" || path === "/builder-chat" ||
                path === "/auto-landing"
                ? "flex-1 min-h-0"
                : "flex-grow"
            }`}
          >
            <Router>
              {routes.map(({ path, component }) => (
                <Route path={path} component={component} />
              ))}
              {<Route default component={NotFound} />}
            </Router>
          </div>
        </AppErrorBoundary>
        {path !== "/" && path !== "/chat" && path !== "/builder-chat" &&
          path !== "/auto-landing" && <LegalFooter />}
      </div>
    </>
  );
};

export const App_ = () => (
  <LocationProvider>
    <App />
  </LocationProvider>
);

if (typeof window !== "undefined") {
  if (
    typeof document !== "undefined" &&
    !prerenderedRoutes.includes(location.pathname)
  ) {
    document.querySelector("script[type=isodata]")?.remove();
  }

  hydrate(<App_ />);

  document.getElementById("hide-prerender")?.remove();
}
